<script>
exports.UsersService = (api, handleError) => {
  const basePath = '/users'
  const adminPath = `/admin${basePath}`

  const RESPONSECACHE = {}

  // Who is signed in, according to the API. Replaces both the old
  // POST /users/auth (the browser no longer holds an Azure token to post) and
  // the GET /users/me boot probe. Returns the same payload shape as /users/me
  // because it reuses that endpoint's controller server-side.
  const fetchSession = async (config = {}) => {
    try {
      return await api.get('/auth/session', config)
    } catch (error) {
      return handleError(error)
    }
  }

  const fetchCurrentUser = async (config = {}) => {
    try {
      return await api.get(`${basePath}/me`, config)
    } catch (error) {
      return handleError(error)
    }
  }

  const getUsersByRole = async (role = null, settings = {}) => {
    try {
      const isValidRoleQuery =
        role.split(',').filter((r) => r?.trim()?.length).length > 0
      if (!isValidRoleQuery) {
        return {}
      }
      const settingsQuery = []
      for (let key in settings) {
        if (settings[key]) {
          settingsQuery.push(`${key}=${settings[key]}`)
        }
      }
      return await api.get(
        `${basePath}/roles/${role}${
          settingsQuery.length ? '?' + settingsQuery.join('&') : ''
        }`
      )
    } catch (error) {
      return handleError(error)
    }
  }

  const signOut = async () => {
    try {
      return await api.post('/auth/logout')
    } catch (error) {
      return handleError(error)
    }
  }

  const createUser = async ({ firstName, lastName, email, role }) => {
    try {
      return await api.post(adminPath, {
        firstName,
        lastName,
        email,
        role
      })
    } catch (error) {
      return handleError(error)
    }
  }

  const getUsersForAdmin = async (role = null) => {
    try {
      return await api.get(`${adminPath}/roles/${role}`)
    } catch (error) {
      return handleError(error)
    }
  }

  const getList = async (settings = {}) => {
    try {
      if (Object.keys(RESPONSECACHE).includes('userslist') && settings.cache) {
        return RESPONSECACHE['userslist']
      }
      const req = await api.get(`${adminPath}/list`)
      if (settings.cache) {
        RESPONSECACHE['userslist'] = req
      }
      return req
    } catch (error) {
      return handleError(error)
    }
  }

  const getRolesList = async () => {
    try {
      return await api.get(`${adminPath}/roles-list`)
    } catch (error) {
      return handleError(error)
    }
  }

  const getReports = async ({
    projectUid,
    userUid,
    from = '2019-08-03',
    to = Date.now()
  }) => {
    try {
      const queryParams = []
      if (projectUid) {
        queryParams.push(`project=${projectUid}`)
      }
      if (userUid) {
        queryParams.push(`user=${userUid}`)
      }
      const queryString = queryParams.length ? '?' + queryParams.join('&') : ''
      return await api.get(`${adminPath}/reports/${from}/${to}${queryString}`)
    } catch (error) {
      return handleError(error)
    }
  }

  const updateUser = async ({ uid, data }) => {
    try {
      const toUpdate = {}
      for (let key in data) {
        switch (key) {
          case 'role':
          case 'birthday':
          case 'active':
          case 'staffing':
          case 'leaveAllowances':
            toUpdate[key] = data[key]
            break
        }
      }
      return await api.patch(`${adminPath}/uid/${uid}`, toUpdate)
    } catch (error) {
      console.log(error)
      return handleError(error)
    }
  }

  const getNotificationInfo = async (notifUID) => {
    try {
      return await api.get(`${basePath}/notifications/uid/${notifUID}`)
    } catch (error) {
      return handleError(error)
    }
  }

  const markNotificationAsRead = async (notifUID = null) => {
    try {
      return await api.patch(
        `${basePath}/notifications${notifUID ? `/uid/${notifUID}` : '/all'}`,
        {
          status: 'READ'
        }
      )
    } catch (error) {
      return handleError(error)
    }
  }

  return {
    fetchSession,
    fetchCurrentUser,
    getUsersByRole,
    getNotificationInfo,
    markNotificationAsRead,
    signOut,
    Admin: {
      createUser,
      getUsersForAdmin,
      getList,
      getRolesList,
      getReports,
      updateUser
    }
  }
}
</script>
